"use client";

import * as Sentry from "@sentry/nextjs";
import { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/components/providers/FetchToastProvider";
import { useMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/useUser";
import { createClient } from "@/utils/supabase/client";
import {
  clearPersistedState,
  readPersistedState,
  useSupportChatPersistence,
} from "../_hooks/useSupportChatPersistence";
import { useDocumentVisible } from "../_hooks/useDocumentVisible";
import { useSupportChatSocket } from "../_hooks/useSupportChatSocket";
import { useUnreadTitle } from "../_hooks/useUnreadTitle";
import { createChime } from "../_logic/chime";
import { closeSupportChat } from "../_logic/close-chat";
import { CHIME_THROTTLE_MS, MAX_MESSAGE_LENGTH } from "../_logic/constants";
import { extractEventIdFromPath } from "../_logic/extract-event-id";
import { insertAdminMessage } from "../_logic/insert-admin-message";
import { toSafeAdminMessage } from "../_logic/safe-admin-message";
import { sendSupportChatMessage } from "../_logic/send-message";
import { fetchSupportThread, markSupportThreadRead } from "../_logic/support-thread-api";
import { countUnread, laterCursor, latestAdminTs, mergeServerThread } from "../_logic/thread-state";
import { AdminMessage, Message, SendFailureKind, ThreadState, ThreadStatus, UserMessage } from "../_logic/types";

export interface SupportChatContextValue {
  isOpen: boolean;
  openWidget: () => void;
  /** Hide the widget but keep the floating launcher (minimize). */
  closeWidget: () => void;
  /** Hide the widget AND the launcher — full dismissal. Sidebar still reopens. */
  dismissWidget: () => void;
  toggleWidget: () => void;
  /** True when the user fully dismissed via X; gates the floating launcher. */
  dismissed: boolean;
  messages: Message[];
  sendMessage: (text: string) => Promise<void>;
  retrySendMessage: (id: string) => Promise<void>;
  threadStatus: ThreadStatus;
  startNewChat: () => void;
  /** Sends POST /support-chat/close. Fire-and-forget; WS event drives UI transition. */
  endChat: () => Promise<void>;
  isMobile: boolean;
  /** Admin replies the planner hasn't read. */
  unreadCount: number;
  /** Marks every reply read here and on the server (which clears the other tabs). No-op when nothing is unread. */
  markRead: () => void;
}

export const SupportChatContext = createContext<SupportChatContextValue | null>(null);

const EMPTY_THREAD: ThreadState = { messages: [], threadStatus: "idle", readThrough: null };

const getAccessToken = async (): Promise<string | null> => {
  const {
    data: { session },
  } = await createClient().auth.getSession();
  return session?.access_token ?? null;
};

/**
 * Owns the live-chat state for the enterprise session: messages, thread status,
 * read state, widget open/closed, and the socket + persistence wiring. The
 * server's stored thread is the source of truth, refetched on mount, on each
 * socket (re)connect and whenever the tab comes back; localStorage is the
 * instant first paint. Replies are marked read while the widget is open in a
 * visible tab; otherwise they chime, count on the badges, and prefix the title.
 * Mounts inside the enterprise shell so its lifetime spans all `/dashboard/*` routes.
 *
 * @param props - Children rendered with access to the chat context.
 * @returns Provider wrapping `children` with `SupportChatContext`.
 */
export const SupportChatProvider = ({ children }: { children: ReactNode }) => {
  // Hooks
  const { user } = useUser();
  const { showToast } = useToast();
  const userId = user?.user_id ?? null;
  const isMobile = useMobile();
  const isVisible = useDocumentVisible();

  // States
  // One object so a server refetch replaces messages, status and read cursor in a single update.
  const [thread, setThread] = useState<ThreadState>(EMPTY_THREAD);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [chime] = useState(() => createChime(CHIME_THROTTLE_MS));

  // Refs (mirror state so callbacks avoid stale closures without bloating deps)
  const threadRef = useRef<ThreadState>(thread);
  const isOpenRef = useRef(isOpen);
  const hasFirstAdminReplyRef = useRef(false);
  const previousUserIdRef = useRef<number | null>(null);
  const refreshRef = useRef<{ running: boolean; again: boolean }>({ running: false, again: false });
  // Bumped on sign-out, account switch and "New chat", so a fetch started before one can't land after it.
  const sessionRef = useRef(0);
  // Bumped on every read, so a refresh that started before it can't restore the old unread count.
  const readEpochRef = useRef(0);
  // Sends that finished, and replies the socket delivered, while the current refresh was out: its snapshot may predate them.
  const changedDuringFetchRef = useRef(new Set<string>());
  // Set by "New chat": the server still answers with the old closed thread until the planner's first message opens one.
  const freshChatRef = useRef(false);

  // Derived values
  const { messages, threadStatus } = thread;
  const unreadCount = useMemo(() => countUnread(thread.messages, thread.readThrough), [thread]);

  // Effects (hydration — must run before handlers reference the persisted state)
  useEffect(() => {
    threadRef.current = thread;
  }, [thread]);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (userId == null) return;
    const prev = previousUserIdRef.current;
    if (prev !== null && prev !== userId) {
      clearPersistedState(prev);
      sessionRef.current += 1;
      freshChatRef.current = false;
      setThread(EMPTY_THREAD);
      setIsOpen(false);
      hasFirstAdminReplyRef.current = false;
    }
    previousUserIdRef.current = userId;
    const persisted = readPersistedState(userId);
    if (persisted) {
      // State saved before server history existed lacks the cursor: its messages are legacy, and count as read.
      const isPreServer = persisted.readThrough === undefined;
      setThread({
        messages: isPreServer ? persisted.messages.map((m): Message => ({ ...m, legacy: true })) : persisted.messages,
        threadStatus: persisted.threadStatus,
        readThrough: isPreServer ? latestAdminTs(persisted.messages) : (persisted.readThrough ?? null),
      });
      hasFirstAdminReplyRef.current = persisted.messages.some((m) => m.kind === "admin");
    }
  }, [userId]);

  useSupportChatPersistence({ userId, thread });

  const setMessages = useCallback(
    (update: (prev: Message[]) => Message[]) => setThread((t) => ({ ...t, messages: update(t.messages) })),
    []
  );

  // Handlers
  const openWidget = useCallback(() => {
    setIsOpen(true);
    setDismissed(false);
  }, []);
  const closeWidget = useCallback(() => setIsOpen(false), []);
  const dismissWidget = useCallback(() => {
    setIsOpen(false);
    setDismissed(true);
  }, []);
  const toggleWidget = useCallback(() => {
    setIsOpen((v) => {
      if (!v) setDismissed(false);
      return !v;
    });
  }, []);

  const showToastForFailure = useCallback(
    (kind: SendFailureKind) => {
      if (kind === "rateLimit") showToast("Too many messages — try again in a minute", "error");
      else if (kind === "server" || kind === "network") showToast("Couldn't reach support. Please retry.", "error");
      else if (kind === "auth") showToast("Session expired. Please refresh the page.", "error");
      else if (kind === "validation") showToast("Message couldn't be sent", "error");
    },
    [showToast]
  );

  // Single bounded 401 retry — refresh session and retry once.
  const postMessage = useCallback(
    async (id: string, text: string) => {
      try {
        const supabase = createClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        // Read pathname at send time so the user gets the right event context
        // even if they navigated between opening the widget and sending.
        const eventId = typeof window !== "undefined" ? extractEventIdFromPath(window.location.pathname) : undefined;
        let result = await sendSupportChatMessage({
          text,
          accessToken: session?.access_token ?? "",
          eventId,
        });

        if (!result.ok && result.kind === "auth") {
          const {
            data: { session: refreshed },
          } = await supabase.auth.refreshSession();
          result = await sendSupportChatMessage({
            text,
            accessToken: refreshed?.access_token ?? "",
            eventId,
          });
        }

        if (result.ok) {
          changedDuringFetchRef.current.add(id);
          setMessages((prev) =>
            prev.map((m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "sent" } : m))
          );
        } else {
          const reason = result.kind;
          setMessages((prev) =>
            prev.map(
              (m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "failed", failureReason: reason } : m)
            )
          );
          showToastForFailure(reason);
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "support-chat", phase: "send" } });
        setMessages((prev) =>
          prev.map(
            (m): Message =>
              m.id === id && m.kind === "user" ? { ...m, status: "failed", failureReason: "network" } : m
          )
        );
        showToastForFailure("network");
      }
    },
    [setMessages, showToastForFailure]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;
      if (threadRef.current.threadStatus === "closed") return;

      const id = crypto.randomUUID();
      const optimistic: UserMessage = { id, kind: "user", text: trimmed, status: "sending" };
      // The server marks the thread read on send too: a planner typing has seen everything above.
      setThread((t) => ({
        messages: [...t.messages, optimistic],
        threadStatus: t.threadStatus === "idle" ? "active" : t.threadStatus,
        readThrough: latestAdminTs(t.messages),
      }));

      await postMessage(id, trimmed);
    },
    [postMessage]
  );

  const retrySendMessage = useCallback(
    async (id: string) => {
      const msg = threadRef.current.messages.find((m) => m.id === id);
      if (!msg || msg.kind !== "user") return;
      setMessages((prev) =>
        prev.map(
          (m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "sending", failureReason: undefined } : m)
        )
      );
      await postMessage(id, msg.text);
    },
    [postMessage, setMessages]
  );

  const markRead = useCallback(() => {
    const current = threadRef.current;
    if (countUnread(current.messages, current.readThrough) === 0) return;
    readEpochRef.current += 1;
    setThread((t) => ({ ...t, readThrough: latestAdminTs(t.messages) }));
    void (async () => {
      try {
        const token = await getAccessToken();
        if (token) await markSupportThreadRead(token);
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "support-chat", phase: "read" } });
      }
    })();
  }, []);

  // Coalesces overlapping triggers (mount, connect, tab return) into one fetch plus at most one rerun.
  const refreshThread = useCallback(async () => {
    const gate = refreshRef.current;
    if (gate.running) {
      gate.again = true;
      return;
    }
    gate.running = true;
    try {
      do {
        gate.again = false;
        const session = sessionRef.current;
        const readEpoch = readEpochRef.current;
        const changedDuringFetch = new Set<string>();
        changedDuringFetchRef.current = changedDuringFetch;
        const token = previousUserIdRef.current === null ? null : await getAccessToken();
        const snapshot = token ? await fetchSupportThread(token) : null;
        // A sign-out or account switch mid-fetch makes the answer someone else's.
        if (!snapshot || session !== sessionRef.current) continue;
        if (snapshot.thread?.status === "open") freshChatRef.current = false;
        if (freshChatRef.current && snapshot.thread?.status === "closed") continue;
        if (snapshot.thread?.messages.some((m) => m.kind === "admin")) hasFirstAdminReplyRef.current = true;
        const readDuringFetch = readEpoch !== readEpochRef.current;
        setThread((t) => {
          const merged = mergeServerThread(t, snapshot, changedDuringFetch);
          return readDuringFetch ? { ...merged, readThrough: laterCursor(merged.readThrough, t.readThrough) } : merged;
        });
      } while (gate.again);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "support-chat", phase: "refresh" } });
    } finally {
      gate.running = false;
    }
  }, []);

  const handleSocketMessage = useCallback(
    (payload: AdminMessage) => {
      // Nothing would render — an empty bubble shouldn't land in the thread or auto-open the widget.
      const safePayload = toSafeAdminMessage(payload);
      if (!safePayload) return;
      // Dedupe multi-pod fan-out via composite key. The backend sends
      // id = ts = the Slack message ts, so each reply already has its own id;
      // the composite id+ts+text key is kept so a payload that reuses an id
      // (as an older backend did with id = thread_ts) can't silently drop the
      // 2nd+ admin replies. It collapses true fan-out duplicates (all fields
      // match) while letting distinct replies through.
      const key = `${safePayload.id}|${safePayload.ts}|${safePayload.text}`;
      const isDuplicate = (messages: Message[]) =>
        messages.some((m) => m.kind === "admin" && `${m.id}|${m.ts}|${m.text}` === key);
      const isLookedAt = isOpenRef.current && document.visibilityState === "visible";
      if (!isDuplicate(threadRef.current.messages) && !isLookedAt) chime.play();
      changedDuringFetchRef.current.add(safePayload.id);
      setMessages((prev) => (isDuplicate(prev) ? prev : insertAdminMessage(prev, safePayload)));
      if (!hasFirstAdminReplyRef.current) {
        hasFirstAdminReplyRef.current = true;
        setIsOpen(true); // auto-open: first admin reply only, per active thread
        setDismissed(false);
      }
    },
    [chime, setMessages]
  );

  const handleSocketClosed = useCallback(() => {
    // Only transition when we currently have an active conversation. After a
    // local reset (idle), late echoes from a user-initiated close are stale
    // and would otherwise render a "closed" indicator on an empty thread.
    setThread((t) => (t.threadStatus === "active" ? { ...t, threadStatus: "closed" } : t));
  }, []);

  const handleSocketRead = useCallback(() => {
    readEpochRef.current += 1;
    setThread((t) => ({ ...t, readThrough: latestAdminTs(t.messages) }));
  }, []);

  const handleSignOut = useCallback(() => {
    const id = previousUserIdRef.current;
    if (id != null) clearPersistedState(id);
    sessionRef.current += 1;
    setThread(EMPTY_THREAD);
    setIsOpen(false);
    setDismissed(false);
    hasFirstAdminReplyRef.current = false;
    freshChatRef.current = false;
    previousUserIdRef.current = null;
  }, []);

  const startNewChat = useCallback(() => {
    if (userId != null) clearPersistedState(userId);
    sessionRef.current += 1;
    freshChatRef.current = true;
    setThread(EMPTY_THREAD);
    setDismissed(false);
    hasFirstAdminReplyRef.current = false;
  }, [userId]);

  const endChat = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      await closeSupportChat(token);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "support-chat", phase: "close" } });
    }
  }, []);

  const handleSocketConnect = useCallback(() => void refreshThread(), [refreshThread]);

  useSupportChatSocket({
    enabled: userId != null,
    onMessage: handleSocketMessage,
    onClosed: handleSocketClosed,
    onRead: handleSocketRead,
    onConnect: handleSocketConnect,
    onSignOut: handleSignOut,
  });

  // Effects
  // Mount, and every return to the tab: a reply may have landed while it was hidden or offline.
  useEffect(() => {
    if (userId != null && isVisible) void refreshThread();
  }, [userId, isVisible, refreshThread]);

  // Open in a visible tab is being read, including a reply that arrives while it is.
  useEffect(() => {
    if (isOpen && isVisible && unreadCount > 0) markRead();
  }, [isOpen, isVisible, unreadCount, markRead]);

  useUnreadTitle(unreadCount, isVisible);

  // Render
  // Memoize so unrelated parent re-renders (e.g. Zustand user-store ticks)
  // don't cascade context updates through every consumer.
  const value = useMemo<SupportChatContextValue>(
    () => ({
      isOpen,
      openWidget,
      closeWidget,
      dismissWidget,
      toggleWidget,
      dismissed,
      messages,
      sendMessage,
      retrySendMessage,
      threadStatus,
      startNewChat,
      endChat,
      isMobile,
      unreadCount,
      markRead,
    }),
    [
      isOpen,
      openWidget,
      closeWidget,
      dismissWidget,
      toggleWidget,
      dismissed,
      messages,
      sendMessage,
      retrySendMessage,
      threadStatus,
      startNewChat,
      endChat,
      isMobile,
      unreadCount,
      markRead,
    ]
  );

  return <SupportChatContext.Provider value={value}>{children}</SupportChatContext.Provider>;
};
